import { formatIsoDay, todayIso } from '@/features/common/calendar';
import type { DateRangeEnd } from '@/features/common/DateRangeFilter/types/DateRangeEnd';
import type { DayMarks } from '@/features/common/DateRangeFilter/types/DayMarks';
import type { PeriodLabels } from '@/features/common/DateRangeFilter/types/PeriodLabels';
import type { CalendarMonth } from '@/features/common/types/CalendarMonth';
import type { DateRangeFilterKey } from '@/i18n/dictionaries/dateRangeFilter';

// What DateRangeFilter decides without a DOM — what the closed control says, which of MUD's state
// classes each calendar day carries, which month and which end the calendar opens on — so it can be
// tested here, where the component itself cannot be rendered.

/** The period's two ends. The calendar fills one at a time: "De la" first, then "Până la". */
export const DATE_RANGE_END = { from: 'from', to: 'to' } as const;

/** Each end's name, as its field's label and the calendar's own name say it. */
export const END_LABEL: Record<DateRangeEnd, DateRangeFilterKey> = { from: 'from', to: 'to' };

/**
 * MUD's state classes for one day: a lone end is `is-selected`; both ends make a range, its ends
 * `is-range-start`/`is-range-end` and the days between `is-in-range`; today is `is-today`.
 */
export function dayClasses(iso: string, { from, to, today }: DayMarks): string {
  const classes = ['date-picker__day'];
  if (iso === today) classes.push('is-today');
  if (from && to) {
    if (iso === from) classes.push('is-range-start');
    if (iso === to) classes.push('is-range-end');
    if (iso > from && iso < to) classes.push('is-in-range');
  } else if (iso === from || iso === to) {
    classes.push('is-selected');
  }
  return classes.join(' ');
}

/**
 * What the closed filter says: nothing chosen, one end, or both.
 *
 * Both ends read as a range; one end reads as the words the panel labels it with ("De la …",
 * "Până la …"), so a half-open period is not mistaken for a single day.
 */
export function periodLabel(from: string | null, to: string | null, labels: PeriodLabels): string {
  if (from && to) return `${formatIsoDay(from)} – ${formatIsoDay(to)}`;
  if (from) return labels.fromValue(formatIsoDay(from));
  if (to) return labels.toValue(formatIsoDay(to));
  return labels.any;
}

/** The month an ISO day falls in — or, for an open end, this month. */
export function monthOf(iso: string | null): CalendarMonth {
  const source = iso ?? todayIso();
  return { year: Number(source.slice(0, 4)), month: Number(source.slice(5, 7)) - 1 };
}

/** The month `delta` months away from the one given, across the edge of a year as Date counts it. */
export function shiftedMonth({ year, month }: CalendarMonth, delta: number): CalendarMonth {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

/** The end the calendar goes to on opening: the one still missing, or "De la". */
export function endToFill(from: string | null, to: string | null): DateRangeEnd {
  return from && !to ? DATE_RANGE_END.to : DATE_RANGE_END.from;
}

/** What an end's field holds when that end changes: the day as MUD writes it, or nothing. */
export function draftOf(iso: string | null): string {
  return iso ? formatIsoDay(iso) : '';
}
